/**
 * Browser-local user preferences.
 *
 * Lightweight key/value over `localStorage`. We don't roll our own settings
 * table on the server yet — these are personal UI choices, not org policy,
 * and per-browser is the natural granularity.
 */

import type { ProductKey } from "./api";

const PREFIX = "kardoe-os:";

function read<T>(key: string, fallback: T, parse?: (s: string) => T): T {
    try {
        const raw = localStorage.getItem(PREFIX + key);
        if (raw === null) return fallback;
        if (parse) return parse(raw);
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

function write(key: string, value: unknown): void {
    try {
        localStorage.setItem(PREFIX + key, JSON.stringify(value));
    } catch {
        // Storage full / SecurityError (private mode) — silently skip.
    }
}

// ---------------------------------------------------------------------------
// Shared product context
// ---------------------------------------------------------------------------

const SELECTED_PRODUCT_KEY = "selectedProduct";

export function getSelectedProduct(): ProductKey | "" {
    const value = read<unknown>(SELECTED_PRODUCT_KEY, "");
    return value === "live-room" || value === "attend" ? value : "";
}

const productListeners = new Set<() => void>();

export function setSelectedProduct(value: ProductKey | ""): void {
    write(SELECTED_PRODUCT_KEY, value);
    for (const cb of productListeners) cb();
}

/** Subscribe to same-tab product changes (for useSyncExternalStore). */
export function subscribeSelectedProduct(cb: () => void): () => void {
    productListeners.add(cb);
    return () => { productListeners.delete(cb); };
}

// ---------------------------------------------------------------------------
// Compose: send as HTML (vs plaintext-only)
// ---------------------------------------------------------------------------

const SEND_HTML_KEY = "composeSendHtml";

export function getSendAsHtml(): boolean {
    return read<boolean>(SEND_HTML_KEY, true);
}

export function setSendAsHtml(v: boolean): void {
    write(SEND_HTML_KEY, v);
}

// ---------------------------------------------------------------------------
// Compose: BCC the archive address on every send
// ---------------------------------------------------------------------------

const BCC_OUTBOUND_KEY = "composeBccOutbound";

export function getBccOutbound(): boolean {
    return read<boolean>(BCC_OUTBOUND_KEY, true);
}

export function setBccOutbound(v: boolean): void {
    write(BCC_OUTBOUND_KEY, v);
}

// ---------------------------------------------------------------------------
// Inbox: offline cache for server-backed per-thread state
// ---------------------------------------------------------------------------

const LAST_SEEN_KEY = "inboxLastSeen";

export type LastSeenMap = Record<string, string>;   // threadKey → ISO timestamp

export function getLastSeenMap(): LastSeenMap {
    return read<LastSeenMap>(LAST_SEEN_KEY, {});
}

export function markThreadSeen(threadKey: string, when: string = new Date().toISOString()): LastSeenMap {
    const map = getLastSeenMap();
    map[threadKey] = when;
    write(LAST_SEEN_KEY, map);
    return map;
}

export function markThreadUnread(threadKey: string): LastSeenMap {
    const map = getLastSeenMap();
    delete map[threadKey];
    write(LAST_SEEN_KEY, map);
    return map;
}

/** Bulk-mark variant — single localStorage write for the "mark all read" action. */
export function markThreadsSeen(threadKeys: string[], when: string = new Date().toISOString()): LastSeenMap {
    const map = getLastSeenMap();
    for (const k of threadKeys) map[k] = when;
    write(LAST_SEEN_KEY, map);
    return map;
}

// ---------------------------------------------------------------------------
// Inbox archive cache
// ---------------------------------------------------------------------------
//
// The server is canonical. localStorage keeps startup/offline behavior fast
// and provides a one-time migration source for users who had state before the
// cross-device mailboxThreadStates resource existed.

const ARCHIVED_KEY = "inboxArchivedThreads";

export type ArchivedSet = Record<string, true>;

export function getArchivedThreads(): ArchivedSet {
    return read<ArchivedSet>(ARCHIVED_KEY, {});
}

export function archiveThread(threadKey: string): ArchivedSet {
    const set = getArchivedThreads();
    set[threadKey] = true;
    write(ARCHIVED_KEY, set);
    return set;
}

export function unarchiveThread(threadKey: string): ArchivedSet {
    const set = getArchivedThreads();
    delete set[threadKey];
    write(ARCHIVED_KEY, set);
    return set;
}

/** Bulk-archive — single localStorage write for the "archive selected" action. */
export function archiveThreads(threadKeys: string[]): ArchivedSet {
    const set = getArchivedThreads();
    for (const k of threadKeys) set[k] = true;
    write(ARCHIVED_KEY, set);
    return set;
}

const MAILBOX_IMPORT_KEY = "mailboxServerStateImported";

/** Snapshot the legacy browser-only state for the one-time server import. */
export function getLegacyMailboxPreferences(): {
    archivedThreadKeys: string[];
    readAtByThread: Array<{ threadKey: string; lastReadAt: string }>;
} | null {
    if (read<boolean>(MAILBOX_IMPORT_KEY, false)) return null;
    const archivedThreadKeys = Object.keys(getArchivedThreads());
    const readAtByThread = Object.entries(getLastSeenMap()).map(([threadKey, lastReadAt]) => ({ threadKey, lastReadAt }));
    return { archivedThreadKeys, readAtByThread };
}

/** Mark the legacy bridge complete only after the server acknowledges it. */
export function markLegacyMailboxPreferencesImported(): void {
    write(MAILBOX_IMPORT_KEY, true);
    try {
        localStorage.removeItem(PREFIX + LAST_SEEN_KEY);
        localStorage.removeItem(PREFIX + ARCHIVED_KEY);
    } catch {
        // A storage-denied browser can safely retry the idempotent import.
    }
}
